import React from 'react';
import { Button, CircularProgress, Stack, TableCell, TableRow, Typography } from '@mui/material';

/**
 * Props for FailedEventsStatusRow.
 * loading   - the failed events list is still being fetched.
 * loadError - the last fetch failed.
 * isEmpty   - the fetch finished and returned no failed events.
 * colSpan   - number of table columns the message row spans.
 * onReload  - fetches the list again (shown as "Try again" after an error).
 */
interface FailedEventsStatusRowProps {
  loading: boolean;
  loadError: boolean;
  isEmpty: boolean;
  colSpan: number;
  onReload: () => void;
}

/**
 * MM-1088: one table row on the DrChrono Failed Events page that tells the user whether the list
 * is still loading, could not be loaded, or has no failed events. Renders nothing when the table
 * has rows to show, so a blank table is never shown without a reason.
 */
export default function FailedEventsStatusRow({
  loading,
  loadError,
  isEmpty,
  colSpan,
  onReload,
}: Readonly<FailedEventsStatusRowProps>) {
  if (!loading && !loadError && !isEmpty) return null;

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 4 }}>
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='center'>
          {loading && (
            <>
              <CircularProgress size={22} />
              <Typography variant='body2'>Loading failed events...</Typography>
            </>
          )}
          {!loading && loadError && (
            <>
              <Typography variant='body2' color='error'>
                Could not load failed events.
              </Typography>
              <Button size='small' variant='outlined' onClick={onReload}>
                Try again
              </Button>
            </>
          )}
          {!loading && !loadError && isEmpty && <Typography variant='body2'>No failed events found.</Typography>}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
